import React, { useState, useMemo, useCallback } from 'react';
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { StakeholderMatch } from '../../services/businessCardService';
import { Avatar, Button, Dialog, DialogFooter, Dot, IconButton, Pill } from '../ui';

// ── Types ──

type FieldState = 'only_existing' | 'only_new' | 'same' | 'conflict';

interface MergeField {
  key: string;
  label: string;
  existingValue: string;
  newValue: string;
  state: FieldState;
  /** Which value the user chose for conflict fields: 'existing' | 'new' */
  chosen: 'existing' | 'new';
}

interface DuplicateReviewSheetProps {
  open: boolean;
  onClose: () => void;
  matches: StakeholderMatch[];
  newData: Record<string, any>;
  onSkip: () => void;
  onMerge: (stakeholderId: string, mergedData: Record<string, any>) => Promise<void>;
}

// ── Field definitions (grouped) ──

const MERGE_FIELD_GROUPS = [
  {
    title: 'Name',
    fields: [
      { key: 'firstName', label: 'First name' },
      { key: 'lastName', label: 'Last name' },
    ],
  },
  {
    title: 'Contact',
    fields: [
      { key: 'professionalEmail', label: 'Work email' },
      { key: 'personalEmail', label: 'Personal email' },
      { key: 'professionalPhone', label: 'Work phone' },
      { key: 'personalPhone', label: 'Personal phone' },
    ],
  },
  {
    title: 'Details',
    fields: [
      { key: 'location', label: 'Location' },
      { key: 'category', label: 'Category' },
    ],
  },
  {
    title: 'Social',
    fields: [
      { key: 'linkedinUrl', label: 'LinkedIn' },
      { key: 'twitterUrl', label: 'X' },
      { key: 'facebookUrl', label: 'Facebook' },
      { key: 'telegramUrl', label: 'Telegram' },
      { key: 'whatsapp', label: 'WhatsApp' },
    ],
  },
];

// ── Helpers ──

function scoreTone(score: number): 'green' | 'amber' | 'neutral' {
  if (score >= 80) return 'green';
  if (score >= 60) return 'amber';
  return 'neutral';
}

function initials(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

function buildMergeFields(
  existingData: Record<string, any>,
  newData: Record<string, any>,
): MergeField[] {
  const fields: MergeField[] = [];

  for (const group of MERGE_FIELD_GROUPS) {
    for (const { key, label } of group.fields) {
      const existingVal = (existingData[key] ?? '').toString().trim();
      const newVal = (newData[key] ?? '').toString().trim();

      let state: FieldState;
      if (existingVal && !newVal) {
        state = 'only_existing';
      } else if (!existingVal && newVal) {
        state = 'only_new';
      } else if (existingVal === newVal) {
        state = 'same';
      } else {
        state = 'conflict';
      }

      fields.push({
        key,
        label,
        existingValue: existingVal,
        newValue: newVal,
        state,
        chosen: state === 'only_new' ? 'new' : 'existing',
      });
    }
  }

  return fields;
}

// ── Component ──

export const DuplicateReviewSheet: React.FC<DuplicateReviewSheetProps> = ({
  open,
  onClose,
  matches,
  newData,
  onSkip,
  onMerge,
}) => {
  const [view, setView] = useState<'list' | 'merge'>('list');
  const [selectedMatch, setSelectedMatch] = useState<StakeholderMatch | null>(null);
  const [mergeFields, setMergeFields] = useState<MergeField[]>([]);
  const [merging, setMerging] = useState(false);

  // Select a match and switch to merge view
  const handleSelectMatch = useCallback((match: StakeholderMatch) => {
    setSelectedMatch(match);
    setMergeFields(buildMergeFields(match.existingData, newData));
    setView('merge');
  }, [newData]);

  // Go back to list
  const handleBack = useCallback(() => {
    setView('list');
    setSelectedMatch(null);
    setMergeFields([]);
  }, []);

  // Toggle a conflict field's chosen value
  const handleFieldChoice = useCallback((fieldKey: string, chosen: 'existing' | 'new') => {
    setMergeFields(prev =>
      prev.map(f => (f.key === fieldKey ? { ...f, chosen } : f)),
    );
  }, []);

  // Build merged data and call onMerge
  const handleConfirmMerge = useCallback(async () => {
    if (!selectedMatch) return;
    setMerging(true);

    try {
      const merged: Record<string, any> = {};

      for (const field of mergeFields) {
        if (field.state === 'only_existing' || field.state === 'same') {
          // Keep existing — no update needed
          continue;
        }
        if (field.state === 'only_new') {
          // Add new data to existing record
          merged[field.key] = field.newValue;
        }
        if (field.state === 'conflict') {
          if (field.chosen === 'new') {
            merged[field.key] = field.newValue;
          }
          // If 'existing' is chosen, no update needed
        }
      }

      await onMerge(selectedMatch.id, merged);
    } finally {
      setMerging(false);
    }
  }, [selectedMatch, mergeFields, onMerge]);

  // Count how many fields will actually change
  const changeCount = useMemo(() => {
    return mergeFields.filter(
      f => f.state === 'only_new' || (f.state === 'conflict' && f.chosen === 'new'),
    ).length;
  }, [mergeFields]);

  // Count conflicts still using existing (user might want to review)
  const conflictCount = useMemo(() => {
    return mergeFields.filter(f => f.state === 'conflict').length;
  }, [mergeFields]);

  const merge = view === 'merge' && selectedMatch;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy="duplicate-review-title"
      handle
      className={merge ? 'h-[92dvh] sm:h-auto sm:max-h-[84vh] sm:max-w-2xl' : 'max-h-[92dvh] sm:max-h-[84vh] sm:max-w-lg'}
    >
      {/* Header */}
      <div className="flex flex-shrink-0 items-center gap-2 px-5 pb-1 pt-1 sm:pt-5">
        {merge && (
          <IconButton label="Back" size="sm" onClick={handleBack} disabled={merging} className="-ml-2">
            <ChevronLeft className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </IconButton>
        )}
        <div className="min-w-0 flex-1">
          <h2 id="duplicate-review-title" className="truncate text-[18px] font-normal tracking-[-0.02em] text-ink">
            {merge ? `Merge into ${selectedMatch.firstName} ${selectedMatch.lastName}` : 'Possible duplicates'}
          </h2>
          <p className="mt-0.5 text-[12px] text-neutral-400">
            {merge
              ? `${changeCount} ${changeCount === 1 ? 'change' : 'changes'}${
                  conflictCount > 0 ? ` · ${conflictCount} ${conflictCount === 1 ? 'conflict' : 'conflicts'}` : ''
                }`
              : `${matches.length} ${matches.length === 1 ? 'match' : 'matches'}`}
          </p>
        </div>
        <IconButton label="Close" size="sm" onClick={onClose} className="-mr-1 bg-neutral-100 text-neutral-600">
          <X className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
      </div>

      {view === 'list' && (
        <MatchListView
          matches={matches}
          onSelect={handleSelectMatch}
          onSkip={onSkip}
        />
      )}
      {merge && (
        <MergeResolutionView
          match={selectedMatch}
          mergeFields={mergeFields}
          onFieldChoice={handleFieldChoice}
          onBack={handleBack}
          onConfirm={handleConfirmMerge}
          merging={merging}
          changeCount={changeCount}
          conflictCount={conflictCount}
        />
      )}
    </Dialog>
  );
};

// ── Match List View ──

interface MatchListViewProps {
  matches: StakeholderMatch[];
  onSelect: (match: StakeholderMatch) => void;
  onSkip: () => void;
}

const MatchListView: React.FC<MatchListViewProps> = ({ matches, onSelect, onSkip }) => {
  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-3 sm:px-5">
        <ul className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
          {matches.map((match) => {
            const contact = [
              match.existingData.professionalEmail || match.existingData.personalEmail,
              match.existingData.professionalPhone || match.existingData.personalPhone,
            ].filter(Boolean);

            return (
              <li key={match.id}>
                <button
                  type="button"
                  onClick={() => onSelect(match)}
                  className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none active:bg-neutral-100"
                >
                  <Avatar initials={initials(match.firstName, match.lastName)} kind="stakeholder" size="md" />
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-[14px] text-ink">
                        {match.firstName} {match.lastName}
                      </span>
                      <span
                        className="inline-flex flex-shrink-0 items-center gap-1 text-[12px] tabular-nums text-neutral-500"
                        title={`${match.score}% match`}
                      >
                        <Dot tone={scoreTone(match.score)} />
                        {match.score}%
                      </span>
                    </span>
                    {contact.length > 0 && (
                      <span className="mt-0.5 block truncate text-[12px] text-neutral-400">{contact.join(' · ')}</span>
                    )}
                    {match.matchReasons.length > 0 && (
                      <span className="mt-1.5 flex flex-wrap gap-1">
                        {match.matchReasons.map((reason, i) => (
                          <Pill key={i} tone="outline">
                            {reason}
                          </Pill>
                        ))}
                      </span>
                    )}
                  </span>
                  <ChevronRight
                    className="h-4 w-4 flex-shrink-0 text-neutral-300 transition-colors group-hover:text-ink"
                    strokeWidth={1.75}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={onSkip} className="h-10 flex-1 sm:h-9 sm:flex-none">
          Create new
        </Button>
      </DialogFooter>
    </>
  );
};

// ── Merge Resolution View ──

interface MergeResolutionViewProps {
  match: StakeholderMatch;
  mergeFields: MergeField[];
  onFieldChoice: (fieldKey: string, chosen: 'existing' | 'new') => void;
  onBack: () => void;
  onConfirm: () => void;
  merging: boolean;
  changeCount: number;
  conflictCount: number;
}

const MergeResolutionView: React.FC<MergeResolutionViewProps> = ({
  mergeFields,
  onFieldChoice,
  onBack,
  onConfirm,
  merging,
  changeCount,
}) => {
  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-3 sm:px-5">
        {/* Column captions (side-by-side from sm) */}
        <div className="hidden grid-cols-[112px_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 px-4 pb-1.5 text-[12px] text-neutral-400 sm:grid">
          <span />
          <span>Current</span>
          <span>New</span>
        </div>

        <div className="space-y-4">
          {MERGE_FIELD_GROUPS.map((group) => {
            const groupFields = mergeFields.filter(f =>
              group.fields.some(gf => gf.key === f.key),
            );
            // Skip groups where all fields are empty on both sides
            const hasContent = groupFields.some(
              f => f.existingValue || f.newValue,
            );
            if (!hasContent) return null;

            return (
              <section key={group.title} aria-label={group.title}>
                <h3 className="px-1 pb-1.5 text-[12px] text-neutral-500">{group.title}</h3>
                <div className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
                  {groupFields.map((field) => (
                    <MergeFieldRow
                      key={field.key}
                      field={field}
                      onChoice={onFieldChoice}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={onBack} disabled={merging} className="hidden sm:inline-flex">
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          loading={merging}
          title={`${changeCount} ${changeCount === 1 ? 'field' : 'fields'} will change`}
          className="h-10 flex-1 sm:h-9 sm:flex-none"
        >
          {!merging && <Check className="h-4 w-4" strokeWidth={2} />}
          {merging ? 'Merging' : 'Merge'}
        </Button>
      </DialogFooter>
    </>
  );
};

// ── Individual Field Row ──

interface MergeFieldRowProps {
  field: MergeField;
  onChoice: (fieldKey: string, chosen: 'existing' | 'new') => void;
}

const MergeFieldRow: React.FC<MergeFieldRowProps> = ({ field, onChoice }) => {
  // Don't render rows where both values are empty
  if (!field.existingValue && !field.newValue) return null;

  const conflict = field.state === 'conflict';

  return (
    <div
      role={conflict ? 'radiogroup' : undefined}
      aria-label={conflict ? field.label : undefined}
      className="grid grid-cols-1 gap-x-3 gap-y-1 px-4 py-2.5 sm:grid-cols-[112px_minmax(0,1fr)_minmax(0,1fr)] sm:items-start"
    >
      <div className="flex min-h-[20px] items-center justify-between gap-2 sm:min-h-[22px]">
        <span className="text-[12px] text-neutral-500 sm:text-[13px]">{field.label}</span>
        <span className="sm:hidden">
          <FieldStateBadge state={field.state} chosen={field.chosen} />
        </span>
      </div>

      {conflict ? (
        <>
          <ChoiceOption field={field} side="existing" onChoice={onChoice} />
          <ChoiceOption field={field} side="new" onChoice={onChoice} />
        </>
      ) : (
        <>
          {/* Current */}
          <ValueCell
            value={field.existingValue}
            className={field.state === 'only_new' ? 'hidden sm:block' : ''}
          />
          {/* New */}
          <ValueCell
            value={field.state === 'same' ? '' : field.newValue}
            className={field.state === 'only_new' ? '' : 'hidden sm:flex'}
            aside={
              field.state === 'same' ? (
                <span className="inline-flex items-center gap-1 text-[12px] text-neutral-400">
                  <Check className="h-3.5 w-3.5" strokeWidth={1.75} />
                  Same
                </span>
              ) : field.state === 'only_new' ? (
                <span className="hidden sm:inline-flex">
                  <Pill tone="green">Add</Pill>
                </span>
              ) : undefined
            }
          />
        </>
      )}
    </div>
  );
};

const ValueCell: React.FC<{ value: string; aside?: React.ReactNode; className?: string }> = ({
  value,
  aside,
  className = '',
}) => (
  <div className={`flex min-h-[22px] min-w-0 items-center gap-2 ${className}`}>
    {value ? (
      <span className="min-w-0 break-words text-[13px] text-ink [overflow-wrap:anywhere]">{value}</span>
    ) : !aside ? (
      <span className="text-[13px] text-neutral-300">—</span>
    ) : null}
    {aside}
  </div>
);

/** Selectable value with a red radio, as in the model picker */
const ChoiceOption: React.FC<{
  field: MergeField;
  side: 'existing' | 'new';
  onChoice: (fieldKey: string, chosen: 'existing' | 'new') => void;
}> = ({ field, side, onChoice }) => {
  const selected = field.chosen === side;
  const value = side === 'existing' ? field.existingValue : field.newValue;

  return (
    <label className="group -mx-2 flex min-w-0 cursor-pointer items-start gap-2.5 rounded-xl px-2 py-1 transition-colors hover:bg-neutral-50 sm:mx-0 sm:-ml-2">
      <input
        type="radio"
        name={`merge-${field.key}`}
        checked={selected}
        onChange={() => onChoice(field.key, side)}
        className="peer sr-only"
      />
      <span
        className={`mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary-600/40 ${
          selected ? 'bg-primary-600 text-white' : 'ring-1 ring-inset ring-neutral-300 group-hover:ring-neutral-400'
        }`}
        aria-hidden="true"
      >
        {selected && <Check className="h-3 w-3" strokeWidth={2.5} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] text-neutral-400 sm:hidden">{side === 'existing' ? 'Current' : 'New'}</span>
        <span
          className={`block break-words text-[13px] [overflow-wrap:anywhere] ${selected ? 'text-ink' : 'text-neutral-400'}`}
        >
          {value}
        </span>
      </span>
    </label>
  );
};

// ── State Badge (phones; the columns say it on larger screens) ──

const FieldStateBadge: React.FC<{ state: FieldState; chosen: 'existing' | 'new' }> = ({
  state,
  chosen,
}) => {
  switch (state) {
    case 'same':
      return <span className="text-[11px] text-neutral-400">Same</span>;
    case 'only_existing':
      return <span className="text-[11px] text-neutral-400">Kept</span>;
    case 'only_new':
      return <Pill tone="green">Add</Pill>;
    case 'conflict':
      return <Pill tone="amber">{chosen === 'new' ? 'Update' : 'Pick'}</Pill>;
  }
};
